import { formatCurrencyInputOnChange, formatDollars } from '@fruitful/fruitils';
import {
    type MembershipProfileAnswerWithQuestion,
    type MembershipProfileSpendingFeelingAnswer,
    SpendingFeeling,
} from '@fruitful/member-profile';
import type { MembershipSpendUpsertDto } from '@fruitful/member-profile/src/spend/types/Spend';
import { SpendType } from '@fruitful/member-profile/src/spend/types/Spend';
import React, { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    CheckIcon,
    EditIcon,
    Icon,
    IconButton,
    Input,
    RotateCcwIcon,
    SaveIcon,
    Spinner,
    useToastController,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import type { MembershipDetails } from '../../entities/entityTypes';
import useUpsertMembershipSpendAndRefetch from '../hooks/useUpsertMembershipSpend';

const formatSpendingFeeling = (feeling: SpendingFeeling): string => {
    switch (feeling) {
        case SpendingFeeling.SHORT:
            return 'I spend beyond my means';
        case SpendingFeeling.HAVE_MONEY_LEFT_OVER:
            return 'I have money left over';
        case SpendingFeeling.BREAK_EVEN:
            return 'I usually break even';
        default:
            return feeling;
    }
};

export const MembershipSpendInfo = ({
    membershipDetails,
    spendingFeelingAnswer,
}: {
    membershipDetails: MembershipDetails;
    spendingFeelingAnswer?: MembershipProfileAnswerWithQuestion<MembershipProfileSpendingFeelingAnswer>;
}) => {
    const toast = useToastController();

    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [amount, setAmount] = useState(
        formatDollars(membershipDetails.membershipSpend?.amount),
    );

    const toggleEditing = () => {
        setIsEditing((prev) => !prev);
    };

    const { upsertMembershipSpendAsync } =
        useUpsertMembershipSpendAndRefetch(membershipDetails);

    const saveSpend = async (amount: string) => {
        const convertedAmount = parseFloat(amount.replace(/[^0-9.-]+/g, ''));

        const existingPeriod = membershipDetails.membershipSpend?.period;
        const requestBody: MembershipSpendUpsertDto = {
            amount: convertedAmount,
            confirmed: true,
            type: SpendType.AUTO,
            id: membershipDetails.membershipSpend?.id,
            membershipId: membershipDetails.id,
            period:
                existingPeriod != null ? new Date(existingPeriod) : new Date(),
        };

        try {
            await upsertMembershipSpendAsync(membershipDetails.id, requestBody);
            toast.show('Spend amount updated', {
                icon: (
                    <View
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="$green1000"
                        borderRadius={12}
                        padding={9}
                    >
                        <Icon icon={CheckIcon} size={24} color="$white" />
                    </View>
                ),
            });
            setIsLoading(false);
        } catch (e) {
            console.error(e);
            toast.show('Error updating spend. Please try again.', {
                icon: (
                    <View
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="$red1000"
                        borderRadius={12}
                        padding={9}
                    >
                        <Icon icon={XIcon} size={24} color="$white" />
                    </View>
                ),
            });
            setIsLoading(false);
        }

        setIsEditing((prev) => !prev);
    };

    return (
        <YStack
            borderRadius="$4"
            backgroundColor="$white"
            padding="$5"
            gap="$4"
            shadowColor="$orange800"
            shadowOffset={{ width: 0, height: 1 }}
            shadowOpacity={0.5}
            shadowRadius={4}
        >
            <YStack
                borderBottomWidth={1}
                borderColor="$gray200"
                paddingBottom="$2"
                marginBottom="$4"
            >
                <XStack
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    marginTop={-4}
                >
                    <BodyLargeEmphasized>Spend</BodyLargeEmphasized>

                    {isLoading ? (
                        <Spinner />
                    ) : isEditing ? (
                        <XStack gap={8}>
                            <IconButton
                                variant="secondary"
                                icon={SaveIcon}
                                onPress={() => {
                                    setIsLoading(true);
                                    void saveSpend(amount);
                                }}
                                disabled={isLoading}
                            />
                            <IconButton
                                variant="secondary"
                                icon={RotateCcwIcon}
                                onPress={() => {
                                    setIsLoading(false);
                                    setIsEditing(false);
                                    const oldAmount =
                                        formatCurrencyInputOnChange(
                                            amount ?? '$',
                                            (
                                                membershipDetails
                                                    .membershipSpend?.amount ??
                                                0
                                            ).toString(),
                                        ).replace(' ', '');
                                    setAmount(oldAmount);
                                }}
                                disabled={isLoading}
                            />
                        </XStack>
                    ) : (
                        <IconButton
                            variant="secondary"
                            icon={EditIcon}
                            onPress={toggleEditing}
                            disabled={isLoading}
                        />
                    )}
                </XStack>
            </YStack>
            {spendingFeelingAnswer?.answer.answer != null && (
                <XStack gap={8} alignItems="center" marginBottom="$4">
                    <BodyMediumEmphasized>
                        Spending Feeling (3 Magic Numbers):
                    </BodyMediumEmphasized>
                    <BodyLargeEmphasized color="$gray1000" fontStyle="italic">
                        {formatSpendingFeeling(
                            spendingFeelingAnswer.answer.answer,
                        )}
                    </BodyLargeEmphasized>
                </XStack>
            )}
            <YStack gap={16}>
                <XStack
                    gap={8}
                    justifyContent="space-between"
                    alignItems="center"
                    backgroundColor="$gray50"
                    paddingRight="$4"
                    paddingVertical="$3"
                >
                    <XStack gap={8} alignItems="center">
                        <BodyMediumEmphasized>
                            Average monthly spend amount:
                        </BodyMediumEmphasized>
                        {isEditing ? (
                            <Input
                                value={amount}
                                onChangeText={(text: string) => {
                                    const newAmount =
                                        formatCurrencyInputOnChange(
                                            amount ?? '$',
                                            text,
                                        ).replace(' ', '');
                                    setAmount(newAmount);
                                }}
                                keyboardType="numeric"
                                width={120}
                                fontSize={16}
                                disabled={isLoading}
                            />
                        ) : (
                            <BodyLargeEmphasized>{amount}</BodyLargeEmphasized>
                        )}
                    </XStack>
                </XStack>
            </YStack>
        </YStack>
    );
};
